// ---------------------------------------------------------
// Datei: MaintenanceProtocols.tsx
// Zweck: Mobile Historie und Erfassung wiederkehrender Serviceprotokolle.
// Kontext: Finale Protokolle bleiben als Nachweis unveränderlich; Entwürfe sind editierbar.
// Stand: 2026-09-16 – #1340: Dezimaltrennzeichen während der Eingabe erhalten.
// ---------------------------------------------------------
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  emptyMaintenanceMeasurements,
  maintenanceChecklistDefaults,
  type MaintenanceChecklistStatus,
  type MaintenanceMeasurements,
  type MaintenanceProtocol,
  type MaintenanceProtocolProjectDetails,
  type MaintenanceProtocolUpsertPayload,
} from '../interfaces/maintenance';
import {
  deleteMaintenanceProtocol,
  finalizeMaintenanceProtocol,
  getMaintenanceProtocols,
  uploadMaintenanceAttachment,
  upsertMaintenanceProtocol,
} from '../services/api/maintenance';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../contexts/QueueContext';
import type { MaintenanceProtocolFinalizeQueueData, ProjectStatusRefreshDetail, QueuedUploadFile } from '../interfaces/queue';
import { isPhotoFile, normalizePhotoFile, PHOTO_EXTENSIONS } from '../services/imageService';
import SignatureDialog from './SignatureDialog';

interface MaintenanceProtocolsProps {
  projectId: string;
  dealName: string;
  projectDetails?: MaintenanceProtocolProjectDetails;
}

const today = (): string => new Date().toISOString().slice(0, 10);

const newProtocol = (projectId: string): MaintenanceProtocol => {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    dealId: projectId,
    maintenanceDate: today(),
    status: 'draft',
    checklist: maintenanceChecklistDefaults(),
    measurements: emptyMaintenanceMeasurements(),
    attachments: [],
    createdByEmail: '',
    createdAtUtc: now,
    updatedAtUtc: now,
  };
};

type SignatureParty = 'employee' | 'customer';

const asPayload = (
  protocol: MaintenanceProtocol,
  signatureParties: ReadonlySet<SignatureParty> = new Set(),
): MaintenanceProtocolUpsertPayload => ({
  maintenanceDate: protocol.maintenanceDate,
  checklist: protocol.checklist,
  measurements: protocol.measurements,
  generalComment: protocol.generalComment,
  ...(signatureParties.has('employee') ? { employeeSignatureDataUrl: protocol.employeeSignatureDataUrl } : {}),
  ...(signatureParties.has('customer') ? { customerSignatureDataUrl: protocol.customerSignatureDataUrl } : {}),
});

const hasRequiredSignatures = (protocol: MaintenanceProtocol): boolean =>
  Boolean(protocol.employeeSignatureDataUrl && protocol.customerSignatureDataUrl);

const numberValue = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const result = Number(value.replace(',', '.'));
  return Number.isFinite(result) ? result : undefined;
};

const displayDate = (value: string): string => new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`));

const displayDateTime = (value: string): string => new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
}).format(new Date(value));

// Drive-Dateien erhalten zur eindeutigen Zuordnung einen technischen Präfix. In der
// Oberfläche zählt dagegen der ursprüngliche, für Monteure lesbare Dateiname.
const displayAttachmentName = (fileName: string): string => {
  const match = fileName.match(/^(?:Wartung|Service)-\d{4}-\d{2}-\d{2}-[\da-f]{32}-(.+)$/i);
  return match?.[1] ?? fileName;
};

const statusLabel: Record<MaintenanceChecklistStatus, string> = {
  ok: 'OK',
  not_ok: 'Nicht OK',
  not_applicable: 'nicht zutreffend',
};

const clientErrorStatus = (cause: unknown): number | undefined => {
  if (typeof cause !== 'object' || cause === null || !('status' in cause)) return undefined;
  const status = (cause as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

const checklistStatuses = Object.keys(statusLabel) as MaintenanceChecklistStatus[];

const isImageAttachment = (attachment: MaintenanceProtocol['attachments'][number]): boolean =>
  attachment.contentType?.startsWith('image/') ?? PHOTO_EXTENSIONS.test(attachment.fileName);

type MaintenanceNumericKey = Exclude<keyof MaintenanceMeasurements, 'outdoorUnitSerialNumber' | 'electricalComponentsReplaced' | 'statisticsReset'>;
type MaintenanceBooleanKey = 'electricalComponentsReplaced' | 'statisticsReset';
const vdeChecklistItemId = 'vde-0701-test';

const isVdeAssessmentMissing = (protocol: MaintenanceProtocol): boolean =>
  protocol.measurements.electricalComponentsReplaced &&
  !['ok', 'not_ok'].includes(protocol.checklist.find((item) => item.id === vdeChecklistItemId)?.status ?? '');

const isOutdoorUnitSerialNumberMissing = (protocol: MaintenanceProtocol): boolean =>
  !protocol.measurements.outdoorUnitSerialNumber?.trim();

const toQueuedFile = (file: File): Promise<QueuedUploadFile> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ name: file.name, type: file.type, size: file.size, dataUrl: String(reader.result) });
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const MaintenanceProtocols: React.FC<MaintenanceProtocolsProps> = ({ projectId, dealName, projectDetails }) => {
  const { addToQueue } = useQueue();
  const { user } = useAuth();
  const [protocols, setProtocols] = useState<MaintenanceProtocol[]>([]);
  const [selected, setSelected] = useState<MaintenanceProtocol | null>(null);
  // Der Rohtext erhält Zwischenstände wie „1,“; das API-Modell bleibt numerisch.
  const [measurementInput, setMeasurementInput] = useState<{ key: MaintenanceNumericKey; value: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signatureTarget, setSignatureTarget] = useState<SignatureParty | null>(null);
  // Mehrere Eingaben können schnell nacheinander erfolgen. Die Kette stellt sicher,
  // dass ein älterer Entwurf nie nach einem neueren an die API gesendet wird.
  const draftSaveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const selectedProtocolRef = useRef<MaintenanceProtocol | null>(null);
  // Signaturen werden nur beim ersten Speichern nach ihrer Erfassung übertragen.
  // Danach bleiben sie serverseitig unverändert und belasten weitere Autosaves nicht.
  const pendingSignaturesRef = useRef<Partial<Record<SignatureParty, string>>>({});
  // Während einer Eingabe existiert noch kein persistierter Snapshot. Ein globaler
  // Queue-Refresh darf diesen lokalen Stand niemals durch den Serverstand ersetzen.
  const hasUnsavedChangesRef = useRef(false);
  const savingOperationsRef = useRef(0);
  const outdoorUnitSerialNumberRef = useRef<HTMLInputElement>(null);

  const isFinalized = selected?.status === 'finalized';
  const canDeleteFinalizedProtocol = user?.isAdmin === true;
  const photoAttachments = useMemo(
    () => selected?.attachments.filter(isImageAttachment) ?? [],
    [selected],
  );
  const photoStorageHint = useMemo(() => {
    if (!selected || photoAttachments.length === 0) return 'Noch keine Bilder hinterlegt';

    // Alte, bereits abgelegte Bilder werden nicht verschoben. Der neutrale Hinweis
    // vermeidet daher einen falschen Ordnerpfad, bis alle Bilder neu hochgeladen sind.
    if (photoAttachments.some((attachment) => /^wartung-/i.test(attachment.fileName)))
      return `${photoAttachments.length} Bild(er) im bestehenden Projektordner`;

    return `${photoAttachments.length} Bild(er) in Service/${selected.maintenanceDate.slice(5, 7)}.${selected.maintenanceDate.slice(0, 4)}`;
  }, [photoAttachments, selected]);
  const documentAttachments = useMemo(
    () => selected?.attachments.filter((attachment) => !isImageAttachment(attachment)) ?? [],
    [selected],
  );
  const orderedProtocols = useMemo(
    () => [...protocols].sort((left, right) => right.maintenanceDate.localeCompare(left.maintenanceDate)),
    [protocols],
  );
  const resolvedProjectDetails = useMemo<MaintenanceProtocolProjectDetails>(() => ({
    ...projectDetails,
    projectName: projectDetails?.projectName?.trim() || dealName.trim() || projectId,
  }), [dealName, projectDetails, projectId]);

  const selectProtocol = (protocol: MaintenanceProtocol | null): void => {
    setMeasurementInput(null);
    selectedProtocolRef.current = protocol;
    setSelected(protocol);
  };

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await getMaintenanceProtocols(projectId);
      setProtocols(result);
      // Ein erfolgreich synchronisierter Offline-Auftrag kann das ausgewählte
      // Protokoll finalisieren, ohne dass die Komponente eine Antwort erhält.
      const selectedId = selectedProtocolRef.current?.id;
      const refreshedSelected = selectedId
        ? result.find((protocol) => protocol.id === selectedId)
        : undefined;
      if (refreshedSelected) {
        selectedProtocolRef.current = refreshedSelected;
        setSelected(refreshedSelected);
      }
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Services konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    const refreshProtocolsAfterQueueSync = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectStatusRefreshDetail>).detail;
      const selectedId = selectedProtocolRef.current?.id;
      const updatesSelectedProtocol = detail?.successfulItems?.some((item) =>
        item.projectId === projectId
        && item.protocolId === selectedId
        && ['maintenance_protocol_upsert', 'maintenance_protocol_attachment', 'maintenance_protocol_finalize'].includes(item.type),
      );

      // Das Event wird für die gesamte Queue ausgelöst. Nur ein erfolgreicher
      // Service-Job für dieses geöffnete Protokoll ist relevant; bei noch fokussierten
      // Formularfeldern bleibt der lokale, noch nicht gespeicherte Stand erhalten.
      if (updatesSelectedProtocol && !hasUnsavedChangesRef.current) void reload();
    };
    window.addEventListener('project-status-refresh', refreshProtocolsAfterQueueSync);
    return () => window.removeEventListener('project-status-refresh', refreshProtocolsAfterQueueSync);
  }, [projectId, reload]);

  // Speichervorgänge können sich überlappen (z. B. Entwurf vor einem Upload).
  // Der Zähler hält den Hinweis sichtbar, bis auch der letzte Vorgang beendet ist.
  const beginSaving = useCallback((label: string): (() => void) => {
    savingOperationsRef.current += 1;
    setSaving(true);
    setSavingLabel(label);
    return () => {
      savingOperationsRef.current -= 1;
      if (savingOperationsRef.current <= 0) {
        savingOperationsRef.current = 0;
        setSaving(false);
        setSavingLabel(null);
      }
    };
  }, []);

  const updateSelected = (update: (current: MaintenanceProtocol) => MaintenanceProtocol): MaintenanceProtocol | null => {
    const current = selectedProtocolRef.current;
    if (!current) return null;
    const next = update(current);
    hasUnsavedChangesRef.current = true;
    selectedProtocolRef.current = next;
    setSelected(next);
    return next;
  };

  const pendingSignatureParties = (protocol: MaintenanceProtocol): Set<SignatureParty> => {
    const parties = new Set<SignatureParty>();
    if (pendingSignaturesRef.current.employee !== undefined
      && pendingSignaturesRef.current.employee === protocol.employeeSignatureDataUrl)
      parties.add('employee');
    if (pendingSignaturesRef.current.customer !== undefined
      && pendingSignaturesRef.current.customer === protocol.customerSignatureDataUrl)
      parties.add('customer');
    return parties;
  };

  const markSignaturesPersisted = (protocol: MaintenanceProtocol, parties: ReadonlySet<SignatureParty>): void => {
    const next = { ...pendingSignaturesRef.current };
    parties.forEach((party) => {
      const signature = party === 'employee' ? protocol.employeeSignatureDataUrl : protocol.customerSignatureDataUrl;
      if (next[party] === signature) delete next[party];
    });
    pendingSignaturesRef.current = next;
  };

  const persistDraft = async (protocol: MaintenanceProtocol): Promise<MaintenanceProtocol> => {
    if (protocol.status === 'finalized') return protocol;
    const finishSaving = beginSaving('Entwurf wird gespeichert …');
    const signatureParties = pendingSignatureParties(protocol);
    const payload = asPayload(protocol, signatureParties);
    try {
      const saved = await upsertMaintenanceProtocol(projectId, protocol.id, payload);
      markSignaturesPersisted(protocol, signatureParties);
      // Eine neuere Eingabe darf nicht durch eine verspätete API-Antwort überschrieben werden.
      if (selectedProtocolRef.current === protocol) {
        selectProtocol(saved);
        hasUnsavedChangesRef.current = false;
      }
      setProtocols((current) => [...current.filter((item) => item.id !== saved.id), saved]);
      setError(null);
      return saved;
    } catch (cause) {
      try {
        const result = await addToQueue('maintenance_protocol_upsert', { projectId, protocolId: protocol.id, payload });
        // Code-Review-Finding: addToQueue kann normal AUFLÖSEN, obwohl der sofortige
        // Online-Retry-Versuch endgültig fehlgeschlagen ist (z.B. derselbe 4xx wie eben) -
        // das Item wird dann von processQueue bereits entfernt (`permanentlyFailed`), ohne
        // dass addToQueue dafür rejected. Ohne diese Prüfung würde hier trotzdem
        // "vorgemerkt" angezeigt, obwohl weder Server noch Queue die Änderung haben.
        if (result && result.permanentlyFailed.length > 0) {
          throw new Error('Serviceprotokoll konnte nicht gespeichert werden.');
        }
        markSignaturesPersisted(protocol, signatureParties);
        if (selectedProtocolRef.current === protocol) hasUnsavedChangesRef.current = false;
        setError('Serviceprotokoll ist für die spätere Synchronisierung vorgemerkt.');
      } catch (queueCause) {
        // Auch die Queue-Ablage kann jetzt fehlschlagen (z.B. Speicherplatz voll, siehe
        // #1199) - zentral hier abgefangen, statt in jedem der vielen `void
        // queueDraftSave(...)`-Aufrufer einzeln, sonst würde der Entwurf sowohl serverseitig
        // als auch lokal soundlos verloren gehen.
        console.error('[MaintenanceProtocols] Entwurf konnte nicht in die Queue geschrieben werden:', queueCause);
        setError(
          queueCause instanceof Error
            ? queueCause.message
            : 'Serviceprotokoll konnte nicht gespeichert werden.'
        );
      }
      return protocol;
    } finally {
      finishSaving();
    }
  };

  const queueDraftSave = (protocol: MaintenanceProtocol): Promise<MaintenanceProtocol> => {
    const queuedSave = draftSaveChainRef.current.then(() => persistDraft(protocol));
    draftSaveChainRef.current = queuedSave.then(() => undefined, () => undefined);
    return queuedSave;
  };

  const save = async (): Promise<MaintenanceProtocol | null> => {
    const protocol = selectedProtocolRef.current;
    if (!protocol) return null;
    return queueDraftSave(protocol);
  };

  const removeProtocol = async (): Promise<void> => {
    const protocol = selectedProtocolRef.current;
    if (!protocol || saving) return;
    if (protocol.status === 'finalized' && !canDeleteFinalizedProtocol) return;

    const confirmation = protocol.status === 'finalized'
      ? 'Bist du sicher, dass du diesen Serviceauftrag löschen möchtest?'
      : 'Sind Sie sicher, dass Sie den Entwurf löschen möchten?';
    if (!window.confirm(confirmation)) return;

    // Ein noch nicht gespeicherter Entwurf existiert nur im Browser und kann ohne API-Aufruf verworfen werden.
    if (!protocols.some((item) => item.id === protocol.id)) {
      selectProtocol(null);
      setError(null);
      return;
    }

    const finishSaving = beginSaving(protocol.status === 'finalized' ? 'Service wird gelöscht …' : 'Entwurf wird gelöscht …');
    try {
      await deleteMaintenanceProtocol(projectId, protocol.id);
      setProtocols((current) => current.filter((item) => item.id !== protocol.id));
      selectProtocol(null);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Das Serviceprotokoll konnte nicht gelöscht werden.');
    } finally {
      finishSaving();
    }
  };

  const finalize = async (): Promise<void> => {
    const current = selectedProtocolRef.current;
    if (!current) return;
    if (!hasRequiredSignatures(current)) {
      setError('Bitte die Unterschriften von Mitarbeiter und Kunde erfassen.');
      return;
    }
    const saved = await save();
    if (!saved) return;
    if (isVdeAssessmentMissing(saved)) {
      setError('Bei getauschten elektrischen Bauteilen bitte die VDE-0701-Prüfung mit OK oder Nicht OK bewerten.');
      return;
    }
    if (isOutdoorUnitSerialNumberMissing(saved)) {
      setError('Bitte die Seriennummer der Außeneinheit eintragen.');
      outdoorUnitSerialNumberRef.current?.focus();
      outdoorUnitSerialNumberRef.current?.reportValidity();
      return;
    }
    const storageDealName = dealName.trim() || projectId;
    const finishSaving = beginSaving('PDF wird erstellt und im Drive abgelegt …');
    try {
      const finalized = await finalizeMaintenanceProtocol(projectId, saved.id, storageDealName, resolvedProjectDetails);
      selectProtocol(finalized);
      setProtocols((current) => [...current.filter((item) => item.id !== finalized.id), finalized]);
      setError(null);
    } catch (cause) {
      const status = clientErrorStatus(cause);
      if (status && status >= 400 && status < 500) {
        setError(cause instanceof Error ? cause.message : 'Das Serviceprotokoll kann noch nicht finalisiert werden.');
        return;
      }
      try {
        const result = await addToQueue('maintenance_protocol_finalize', {
          projectId,
          protocolId: saved.id,
          dealName: storageDealName,
          projectDetails: resolvedProjectDetails,
          payload: asPayload(saved),
        } satisfies MaintenanceProtocolFinalizeQueueData);
        // Siehe persistDraft weiter oben: ein sofortiger endgültiger Fehlschlag lässt
        // addToQueue normal auflösen (nur mit permanentlyFailed statt successful).
        if (result && result.permanentlyFailed.length > 0) {
          throw new Error('Serviceprotokoll konnte nicht finalisiert werden.');
        }
        setError('Finalisierung ist für die spätere Synchronisierung vorgemerkt.');
      } catch (queueCause) {
        // Siehe persistDraft weiter oben: addToQueue kann jetzt bei einem echten
        // Persistenzfehler (z.B. Speicherplatz voll) werfen (#1199) - ohne diesen catch
        // würde die Finalisierung sowohl serverseitig als auch lokal spurlos verloren gehen.
        console.error('[MaintenanceProtocols] Finalisierung konnte nicht in die Queue geschrieben werden:', queueCause);
        setError(
          queueCause instanceof Error
            ? queueCause.message
            : 'Serviceprotokoll konnte nicht finalisiert werden.'
        );
      }
    } finally {
      finishSaving();
    }
  };

  const saveSignature = (dataUrl: string): void => {
    const target = signatureTarget;
    if (!target) return;
    const updated = updateSelected((current) => target === 'employee'
      ? { ...current, employeeSignatureDataUrl: dataUrl }
      : { ...current, customerSignatureDataUrl: dataUrl });
    if (updated)
      pendingSignaturesRef.current = { ...pendingSignaturesRef.current, [target]: dataUrl };
    setSignatureTarget(null);
    // Die Signatur bleibt zunächst im lokalen Entwurf und wird beim nächsten
    // regulären Speichern bzw. Finalisieren einmalig mitübertragen.
  };

  const uploadFiles = async (files: File[], label: string, queuedLabel: string): Promise<void> => {
    if (!selectedProtocolRef.current || !files.length || isFinalized) return;
    const saved = await save();
    if (!saved) return;
    // Beim ersten Laden kann der Dealname noch aus dem Cache nachgeladen werden.
    // Die Drive-Ablage braucht trotzdem einen stabilen Ordnernamen.
    const storageDealName = dealName.trim() || projectId;
    const finishSaving = beginSaving(label);
    let uploadedFileCount = 0;
    try {
      let latest = saved;
      for (const file of files) {
        latest = await uploadMaintenanceAttachment(projectId, latest.id, storageDealName, file);
        uploadedFileCount += 1;
      }
      selectProtocol(latest);
      setProtocols((current) => [...current.filter((item) => item.id !== latest.id), latest]);
    } catch (cause) {
      const queuedFiles = await Promise.all(files.slice(uploadedFileCount).map(toQueuedFile));
      if (queuedFiles.length) {
        try {
          const result = await addToQueue('maintenance_protocol_attachment', { projectId, protocolId: saved.id, dealName: storageDealName, files: queuedFiles, payload: asPayload(saved) });
          // Siehe persistDraft weiter oben: ein sofortiger endgültiger Fehlschlag lässt
          // addToQueue normal auflösen (nur mit permanentlyFailed statt successful).
          if (result && result.permanentlyFailed.length > 0) {
            throw new Error(`${queuedLabel} konnten nicht gespeichert werden.`);
          }
          setError(`${queuedLabel} sind für die spätere Synchronisierung vorgemerkt.`);
        } catch (queueCause) {
          // addToQueue kann jetzt bei einem echten Persistenzfehler werfen (z.B.
          // Speicherplatz voll durch die Foto-Anhänge selbst - siehe #1199).
          console.error('[MaintenanceProtocols] Anhänge konnten nicht in die Queue geschrieben werden:', queueCause);
          setError(
            queueCause instanceof Error
              ? queueCause.message
              : `${queuedLabel} konnten nicht gespeichert werden.`
          );
        }
      } else {
        setError(`${queuedLabel} sind für die spätere Synchronisierung vorgemerkt.`);
      }
    } finally {
      finishSaving();
    }
  };

  const attachFiles = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    await uploadFiles(files, 'Dateien werden hochgeladen …', 'Anhänge');
  };

  const attachPhotos = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const originalFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!originalFiles.length || isFinalized) return;
    if (originalFiles.some((file) => !isPhotoFile(file))) {
      setError('Bitte nur Bilddateien auswählen.');
      return;
    }

    const finishPreparing = beginSaving('Bilder werden vorbereitet …');
    try {
      const normalizedFiles = await Promise.all(
        originalFiles.map(async (file) => (await normalizePhotoFile(file, {
          fileName: file.name,
          maxDimension: 2000,
          qualities: [0.86],
        })).file),
      );
      await uploadFiles(normalizedFiles, 'Bilder werden hochgeladen …', 'Bilder');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Bilder konnten nicht vorbereitet werden.');
    } finally {
      finishPreparing();
    }
  };

  const updateMeasurement = (key: MaintenanceNumericKey | MaintenanceBooleanKey, value: number | boolean | undefined, saveImmediately = false): void => {
    const updated = updateSelected((current) => ({
      ...current,
      measurements: { ...current.measurements, [key]: value },
      // Ohne Austausch elektrischer Bauteile ist die VDE-Prüfung nicht zutreffend.
      checklist: key === 'electricalComponentsReplaced' && !value
        ? current.checklist.map((item) => item.id === vdeChecklistItemId ? { ...item, status: 'not_applicable' } : item)
        : current.checklist,
    }));
    if (saveImmediately && updated) void queueDraftSave(updated);
  };

  const updateChecklistStatus = (itemId: string, status: MaintenanceChecklistStatus): void => {
    const updated = updateSelected((current) => ({
      ...current,
      checklist: current.checklist.map((item) => item.id === itemId ? { ...item, status } : item),
    }));
    if (updated) void queueDraftSave(updated);
  };

  const renderChecklistItem = (item: MaintenanceProtocol['checklist'][number]) => (
    <div key={item.id} className="rounded border border-gray-200 p-3 dark:border-gray-700">
      <label className="block text-sm font-medium text-gray-800 dark:text-gray-100">{item.label}</label>
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`Status für ${item.label}`}>
        {checklistStatuses.map((status) => {
          const isSelected = item.status === status;
          return (
            <button
              key={status}
              type="button"
              disabled={isFinalized}
              aria-pressed={isSelected}
              onClick={() => updateChecklistStatus(item.id, status)}
              className={`min-h-10 rounded-full border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
                isSelected
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-primary-400 dark:hover:bg-primary-950'
              }`}
            >
              {statusLabel[status]}
            </button>
          );
        })}
      </div>
      <input
        disabled={isFinalized}
        value={item.comment || ''}
        onChange={(event) => updateSelected((current) => ({ ...current, checklist: current.checklist.map((entry) => entry.id === item.id ? { ...entry, comment: event.target.value || undefined } : entry) }))}
        onBlur={() => {
          const protocol = selectedProtocolRef.current;
          if (protocol) void queueDraftSave(protocol);
        }}
        placeholder="Kommentar (optional)"
        className="mt-2 w-full rounded border p-2 text-sm disabled:bg-gray-100"
      />
    </div>
  );

  return (
    <section className="space-y-4" aria-busy={saving}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Service</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Serviceprotokolle und Nachweise</p>
        </div>
        <button type="button" className="rounded bg-primary-600 px-3 py-2 text-sm font-semibold text-white" onClick={() => selectProtocol(newProtocol(projectId))}>
          Neuer Service
        </button>
      </div>

      {loading ? <p className="text-sm text-gray-500">Services werden geladen …</p> : (
        <div className="space-y-2">
          {orderedProtocols.map((protocol) => (
            <button key={protocol.id} type="button" onClick={() => selectProtocol(protocol)} className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <span className="font-medium text-gray-900 dark:text-white">Service {displayDate(protocol.maintenanceDate)}</span>
              <span className="mt-1 block text-xs text-gray-500">{protocol.status === 'finalized' ? 'Finalisiert' : 'Entwurf'} · {protocol.attachments.length} Anhänge</span>
            </button>
          ))}
          {!orderedProtocols.length && <p className="rounded border border-dashed p-3 text-sm text-gray-500">Noch keine Services dokumentiert.</p>}
        </div>
      )}

      {selected && (
        <div className="space-y-4 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <dl className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Projekt</dt>
              <dd className="mt-1 font-semibold text-gray-900 dark:text-white">{resolvedProjectDetails.projectName}</dd>
            </div>
            {(resolvedProjectDetails.address || resolvedProjectDetails.zip || resolvedProjectDetails.city) && (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Anlagenadresse</dt>
                <dd className="mt-1">
                  {resolvedProjectDetails.address && <span className="block">{resolvedProjectDetails.address}</span>}
                  {(resolvedProjectDetails.zip || resolvedProjectDetails.city) && (
                    <span className="block">{[resolvedProjectDetails.zip, resolvedProjectDetails.city].filter(Boolean).join(' ')}</span>
                  )}
                </dd>
              </div>
            )}
            {(resolvedProjectDetails.contactName || resolvedProjectDetails.contactPhone || resolvedProjectDetails.contactMobilePhone || resolvedProjectDetails.contactEmail) && (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Kundenkontakt</dt>
                <dd className="mt-1 space-y-0.5">
                  {resolvedProjectDetails.contactName && <span className="block">{resolvedProjectDetails.contactName}</span>}
                  {resolvedProjectDetails.contactPhone && <span className="block">Tel.: {resolvedProjectDetails.contactPhone}</span>}
                  {resolvedProjectDetails.contactMobilePhone && <span className="block">Mobil: {resolvedProjectDetails.contactMobilePhone}</span>}
                  {resolvedProjectDetails.contactEmail && <span className="block">{resolvedProjectDetails.contactEmail}</span>}
                </dd>
              </div>
            )}
          </dl>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">{isFinalized ? 'Serviceprotokoll' : 'Service erfassen'}</h3>
            {isFinalized && <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">Finalisiert</span>}
          </div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Datum
            <input disabled={isFinalized} type="date" value={selected.maintenanceDate} onChange={(event) => updateSelected((current) => ({ ...current, maintenanceDate: event.target.value }))} onBlur={() => { const protocol = selectedProtocolRef.current; if (protocol) void queueDraftSave(protocol); }} className="mt-1 w-full rounded border p-2 disabled:bg-gray-100" />
          </label>

          {selected.createdByEmail && (
            <dl className="space-y-1 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
              <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
                <dt className="font-medium">Entwurf erstellt von</dt>
                <dd>{selected.createdByEmail} · {displayDateTime(selected.createdAtUtc)}</dd>
              </div>
              {selected.finalizedByEmail && selected.finalizedAtUtc && (
                <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
                  <dt className="font-medium">Finalisiert von</dt>
                  <dd>{selected.finalizedByEmail} · {displayDateTime(selected.finalizedAtUtc)}</dd>
                </div>
              )}
            </dl>
          )}

          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-white">Checkliste</h4>
            {selected.checklist
              .filter((item) => item.id !== vdeChecklistItemId)
              .map(renderChecklistItem)}
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-white">Messwerte</h4>
            <label className="block text-sm text-gray-700 dark:text-gray-200">Seriennummer Außeneinheit <span className="text-red-600">*</span>
              <input
                ref={outdoorUnitSerialNumberRef}
                disabled={isFinalized}
                required
                aria-required="true"
                aria-invalid={isOutdoorUnitSerialNumberMissing(selected)}
                value={selected.measurements.outdoorUnitSerialNumber || ''}
                onChange={(event) => updateSelected((current) => ({
                  ...current,
                  measurements: { ...current.measurements, outdoorUnitSerialNumber: event.target.value || undefined },
                }))}
                onBlur={() => {
                  const protocol = selectedProtocolRef.current;
                  if (protocol) void queueDraftSave(protocol);
                }}
                placeholder="Seriennummer der Außeneinheit"
                className="mt-1 w-full rounded border p-2 aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-red-500 disabled:bg-gray-100"
              />
              {!isFinalized && isOutdoorUnitSerialNumberMissing(selected) && (
                <span className="mt-1 block text-xs text-red-600 dark:text-red-400">Für das Finalisieren erforderlich.</span>
              )}
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {([
                ['compressorOperatingHours', 'Betriebsstunden Kompressor', 'h'], ['auxiliaryHeaterOperatingHours', 'Betriebsstunden Zusatzheizung', 'h'], ['systemPressureBar', 'Anlagendruck', 'bar'], ['energyYieldHeatingKwh', 'Ertrag Heizung', 'kWh'], ['energyYieldHotWaterKwh', 'Ertrag Trinkwasser', 'kWh'], ['copHeating', 'COP Heizung', ''], ['copHotWater', 'COP Trinkwasser', ''], ['totalElectricityConsumptionKwh', 'Gesamtstromverbrauch', 'kWh'],
              ] as [MaintenanceNumericKey, string, string][]).map(([key, label, unit]) => (
                <label key={key} className="block text-sm text-gray-700 dark:text-gray-200">{label}{unit ? ` (${unit})` : ''}
                  <input
                    type="text"
                    disabled={isFinalized}
                    inputMode="decimal"
                    value={measurementInput?.key === key ? measurementInput.value : selected.measurements[key] ?? ''}
                    onChange={(event) => {
                      setMeasurementInput({ key, value: event.target.value });
                      updateMeasurement(key, numberValue(event.target.value));
                    }}
                    onBlur={() => {
                      setMeasurementInput(null);
                      const protocol = selectedProtocolRef.current;
                      if (protocol) void queueDraftSave(protocol);
                    }}
                    className="mt-1 w-full rounded border p-2 disabled:bg-gray-100"
                  />
                </label>
              ))}
            </div>
            {([
              ['electricalComponentsReplaced', 'Elektrische Bauteile wurden getauscht'], ['statisticsReset', 'Anlagenstatistik nach dem Service zurückgesetzt'],
            ] as [MaintenanceBooleanKey, string][]).map(([key, label]) => (
              <label key={key} className="flex min-h-11 items-center gap-3 text-sm text-gray-700 dark:text-gray-200">
                <input
                  disabled={isFinalized}
                  type="checkbox"
                  checked={Boolean(selected.measurements[key])}
                  onChange={(event) => updateMeasurement(key, event.target.checked, true)}
                  className="!block !h-5 !w-5 !shrink-0 !rounded !p-0 accent-primary-600"
                />
                <span>{label}</span>
              </label>
            ))}
            {selected.measurements.electricalComponentsReplaced &&
              selected.checklist
                .filter((item) => item.id === vdeChecklistItemId)
                .map(renderChecklistItem)}
            {isVdeAssessmentMissing(selected) && (
              <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                Die VDE-0701-Prüfung ist erforderlich. Bitte oben OK oder Nicht OK auswählen.
              </p>
            )}
          </div>

          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Gesamtbemerkung
            <textarea
              disabled={isFinalized}
              value={selected.generalComment || ''}
              onChange={(event) => updateSelected((current) => ({ ...current, generalComment: event.target.value || undefined }))}
              onBlur={() => {
                const protocol = selectedProtocolRef.current;
                if (protocol) void queueDraftSave(protocol);
              }}
              className="mt-1 min-h-24 w-full rounded border p-2 disabled:bg-gray-100"
            />
          </label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Unterschriften <span className="text-red-600">*</span></p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Für die Finalisierung sind beide Unterschriften erforderlich.</p>
            <div className="mt-3 flex flex-wrap gap-4">
              <div>
                <button
                  type="button"
                  disabled={isFinalized || saving}
                  aria-describedby="employee-signature-status"
                  onClick={() => setSignatureTarget('employee')}
                  className="rounded border border-primary-300 px-3 py-2 text-sm font-semibold text-primary-700 disabled:opacity-60 dark:border-primary-700 dark:text-primary-300"
                >
                  {selected.employeeSignatureDataUrl ? 'Unterschrift Mitarbeiter ändern' : 'Unterschrift Mitarbeiter'}
                </button>
                <p id="employee-signature-status" className={`mt-1 text-xs ${selected.employeeSignatureDataUrl ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {selected.employeeSignatureDataUrl ? 'Unterschrift erfasst.' : 'Pflichtfeld: Unterschrift fehlt.'}
                </p>
              </div>
              <div>
                <button
                  type="button"
                  disabled={isFinalized || saving}
                  aria-describedby="customer-signature-status"
                  onClick={() => setSignatureTarget('customer')}
                  className="rounded border border-primary-300 px-3 py-2 text-sm font-semibold text-primary-700 disabled:opacity-60 dark:border-primary-700 dark:text-primary-300"
                >
                  {selected.customerSignatureDataUrl ? 'Unterschrift Kunde ändern' : 'Unterschrift Kunde'}
                </button>
                <p id="customer-signature-status" className={`mt-1 text-xs ${selected.customerSignatureDataUrl ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {selected.customerSignatureDataUrl ? 'Unterschrift erfasst.' : 'Pflichtfeld: Unterschrift fehlt.'}
                </p>
              </div>
            </div>
            {isFinalized && (
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">Unterschriften sind im finalen PDF enthalten.</p>
            )}
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Servicebilder</p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  {photoStorageHint}
                </p>
              </div>
              {!isFinalized && (
                <div className="flex flex-wrap gap-2">
                  <label
                    aria-disabled={saving}
                    className={`rounded-md bg-primary-600 px-3 py-2 text-xs font-medium text-white ${saving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    Foto aufnehmen
                    <input
                      aria-label="Servicebild aufnehmen"
                      disabled={saving}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(event) => void attachPhotos(event)}
                      className="sr-only"
                    />
                  </label>
                  <label
                    aria-disabled={saving}
                    className={`rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 dark:border-gray-600 dark:text-gray-200 ${saving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    Aus Album wählen
                    <input
                      aria-label="Servicebilder aus Album wählen"
                      disabled={saving}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(event) => void attachPhotos(event)}
                      className="sr-only"
                    />
                  </label>
                </div>
              )}
            </div>
            {photoAttachments.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {photoAttachments.map((photo) => {
                  const photoName = displayAttachmentName(photo.fileName);
                  return photo.webViewLink ? (
                    <a
                      key={photo.fileId}
                      href={photo.webViewLink}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
                    >
                      {photo.thumbnailLink ? (
                        <img src={photo.thumbnailLink} alt={photoName} className="mb-2 h-20 w-full rounded object-cover" loading="lazy" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="mb-2 flex h-20 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">Bild</div>
                      )}
                      <span className="block truncate" title={photoName}>{photoName}</span>
                    </a>
                  ) : (
                    <div key={photo.fileId} className="min-w-0 rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200">
                      <div className="mb-2 flex h-20 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">Bild</div>
                      <span className="block truncate" title={photoName}>{photoName}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">PDF und weitere Anhänge</p>
            <input aria-label="PDF und weitere Anhänge auswählen" disabled={isFinalized || saving} type="file" multiple onChange={(event) => void attachFiles(event)} className="mt-1 text-sm" />
            <ul className="mt-2 space-y-2 text-sm">
              {documentAttachments.map((attachment) => {
                const attachmentName = displayAttachmentName(attachment.fileName);
                return (
                  <li key={attachment.fileId} className="flex items-center justify-between gap-3 rounded border border-gray-200 px-3 py-2 dark:border-gray-700">
                    <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">{attachmentName}</span>
                  {attachment.webViewLink && (
                    <a
                      href={attachment.webViewLink}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${attachmentName} öffnen`}
                      title="Dokument öffnen"
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary-200 text-primary-700 transition hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-950"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M14 3h7v7" />
                        <path d="m10 14 11-11" />
                        <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                      </svg>
                    </a>
                  )}
                  </li>
                );
              })}
            </ul>
          </div>
          {(!isFinalized || canDeleteFinalizedProtocol) && (
            <div className="flex flex-wrap gap-2">
              {!isFinalized && (
                <>
                  <button type="button" disabled={saving} onClick={() => void save()} className="rounded border px-3 py-2 text-sm font-semibold">
                    Entwurf speichern
                  </button>
                  <button type="button" disabled={saving} onClick={() => void finalize()} className="rounded bg-primary-600 px-3 py-2 text-sm font-semibold text-white">
                    Finalisieren & PDF ablegen
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={() => void removeProtocol()}
                className="rounded border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
              >
                {isFinalized ? 'Service löschen' : 'Entwurf löschen'}
              </button>
            </div>
          )}
        </div>
      )}
      <SignatureDialog
        isOpen={signatureTarget !== null}
        title={signatureTarget === 'employee' ? 'Unterschrift Mitarbeiter' : 'Unterschrift Kunde'}
        initialSignature={signatureTarget === 'employee' ? selected?.employeeSignatureDataUrl : selected?.customerSignatureDataUrl}
        onCancel={() => setSignatureTarget(null)}
        onSave={saveSignature}
      />
      {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {savingLabel && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-x-4 bottom-20 z-50 mx-auto flex max-w-md items-center gap-3 rounded-lg border border-primary-200 bg-white px-4 py-3 text-sm font-medium text-primary-800 shadow-lg sm:bottom-4 dark:border-primary-800 dark:bg-gray-900 dark:text-primary-200"
        >
          <svg className="h-5 w-5 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.22-8.56" />
          </svg>
          <div>
            <p>{savingLabel}</p>
            <p className="text-xs font-normal text-gray-500 dark:text-gray-400">Bitte warten …</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default MaintenanceProtocols;
